<template>
  <div class="medicine-order-details-watsons-delivery">
    <full-screen-loader position="fixed" v-if="loading"/>

    <div class="flex-col m-b-20">
      <div class="Title1 m-b-5">
        <text-string label-id="txt042"
                     :props="{'strings': [selectedWatson.branchName, dateToCall]}"/>
      </div>
      <text-string label-id="txt266"/>
    </div>

    <div class="smd-form__row justify-content-center">
      <smd-btn class="icon-text view-delivery-call__call-btn"
               section="8"
               @click="showContactNumberModal">
        <span class="ns-icon-ico047"></span>
        <text-string label-id="txt277"
                     :props="{strings: [selectedWatson.branchName]}"/>
      </smd-btn>
    </div>

    <div class="smd-form__row justify-content-center m-b-30"
         :class="{'justify-content-center': $isDesktop}">
      <coupon-countdown-timer :start-time="startTimeString"
                              :end-time="endTimeString"
                              large
                              :trans="$options.transConfig"/>
    </div>

    <div class="smd-form__row m-b-20">
      <img src="@/assets/images/pharmacies/img004.png" width="137px" alt="Watsons Logo">
    </div>

    <div :class="$isDesktop ? 'm-b-15' : 'm-b-5'">
      <div class="flex-col">
        <text-string class="m-b-5" label-id="txt051"/>
        <text-string :label-id="operationalHoursLabelId"/>
      </div>
    </div>

    <div :class="$isDesktop ? 'm-b-15' : 'm-b-5'">
      <div class="flex-col">
        <text-string class="m-b-5" label-id="txt124"/>
        <text-string :label-id="sameDayDeliveryCutOffLabelId"/>
      </div>
    </div>

    <div class="smd-form__row"
         v-if="selectedWatson.mobilenumbers.length">
      <div class="flex-col">
        <text-string class="m-b-5" label-id="txt052"/>
        <div>
        <span v-for="(contact, index) in selectedWatson.mobilenumbers"
              :key="index">
          <span class="Hyperlink1 dodgerBlue">{{ contact }}</span>
          <span class="gray-200" v-if="index !== selectedWatson.mobilenumbers.length - 1"> / </span>
        </span>
        </div>
      </div>
    </div>

    <div class="smd-form__row align-items-center m-t-30">
      <span class="ns-icon-ico015 ns-large-icon"></span>
      <div class="flex-col">
        <text-string class="m-b-5" label-id="txt303" :props="{strings: [redemptionCode]}"/>
        <text-string label-id="txt207"/>
      </div>
    </div>

    <div class="flex-row-center-xy-spaced m-b-30 m-t-30">
      <smd-btn section="4"
               variant="unselected"
               @click="cancelOrder">
        <text-string label-id="txt267"/>
      </smd-btn>
      <smd-btn section="4"
               variant="blue-enabled"
               @click="reportIssue">
        {{ getElementString('txt219') }}
      </smd-btn>
    </div>

    <div class="divider--dotted m-b-30"></div>

    <div v-if="serviceOrder.prescriptions.length" class="added-prescriptions m-b-30">
      <text-string label-id="txt125" class="m-b-15"/>

      <div v-for="(prescription, prescriptionIndex) in serviceOrder.prescriptions"
           :key="prescriptionIndex">
        <prescription-card v-if="prescription.token"
                           :prescription="prescription"
                           :show-coupon="isIssuedCoupon(prescription) && !computedHideCoupon(prescription)"
                           class="m-b-15">
          <div v-if="prescription.remarks && prescription.remarks !== ''" class="remarks">
            <text-string label-id="txt058" class="m-t-10"/>
            <p class="Body1">{{ prescription.remarks }}</p>
          </div>
        </prescription-card>

        <attached-file-card v-if="!prescription.token"
                            class="m-b-15"
                            :prescription="prescription"
                            :orderid="serviceOrder.orderid">
          <div v-if="prescription.remarks && prescription.remarks !== ''" class="remarks">
            <text-string label-id="txt058" class="m-t-10"/>
            <p class="Body1">{{ prescription.remarks }}</p>
          </div>
        </attached-file-card>
      </div>
    </div>

    <div v-if="serviceOrder.orderData && serviceOrder.orderData.length" class="other-items m-b-30">
      <text-string label-id="txt204" class="m-b-15"/>
      <p v-for="(item, itemIndex) in serviceOrder.orderData"
         :key="itemIndex"
         class="Body1">
        {{ item.name }} - {{ item.remarks }}
      </p>
    </div>

    <div v-if="serviceOrder.ids.length" class="sc-pwd-id m-b-30">
      <text-string label-id="txt126" class="m-b-10"/>
      <p v-for="(id, idIndex) in serviceOrder.ids"
         :key="idIndex"
         @click="viewId(id)">
        <text-string label-id="txt140" class="m-b-5"/>
      </p>
    </div>

    <div class="delivery-details">
      <text-string label-id="txt095" class="m-b-10"/>

      <text-string label-id="txt101" class="m-b-5"/>
      <p class="Body1" :class="$isDesktop ? 'm-b-15' : 'm-b-5'">{{ patientName }}</p>
      <text-string label-id="txt102" class="m-b-5"/>
      <p class="Body1" :class="$isDesktop ? 'm-b-15' : 'm-b-5'">{{ serviceOrder.mobilenumber }}</p>
      <text-string v-if="street" label-id="txt103" class="m-b-5"/>
      <p v-if="street" class="Body1" :class="$isDesktop ? 'm-b-15' : 'm-b-5'">{{ street }}</p>
      <text-string v-if="city" label-id="txt104" class="m-b-5"/>
      <p v-if="city" class="Body1 m-b-5">{{ city | formatCity }}</p>
    </div>
  </div>
</template>

<script>
  import {MedicineService} from '@/services/medicine';
  import {
    PHARMACY_OPERATIONAL_HOURS_LABEL_ID,
    GET_PHARMACY_SAME_DAY_DELIVERY_CUTOFF_LABEL_ID,
  } from '@/reference/medicine';

  import GetElementStringMixin from '@/mixins/get-element-string';
  import MedicineOrderDetailsWatsonsMixin from '@/mixins/medicine-order-details-watsons-finished-mixin';
  import ModalContactNumber from '@/views/common/medicine-order-details/modals/modal-contact-number';
  import ModalPreviewFile from '@/modals/modal-preview-file';
  import {isCouponExpired} from "@/utils/medicine";

  export default {
    name: 'medicine-order-details-watsons-delivery',
    mixins: [
      GetElementStringMixin,
      MedicineOrderDetailsWatsonsMixin,
    ],
    filters: {
      formatCity(city) {
        return city.replace('\n', '').replace('\n', ', ');
      },
    },
    data() {
      return {
        loading: false,
      };
    },
    computed: {
      dateToCall() {
        return `<span class="crimson">until ${this.endTime.format('MMM D, YYYY')}</span>`;
      },
      operationalHoursLabelId() {
        return PHARMACY_OPERATIONAL_HOURS_LABEL_ID[this.serviceOrder.partnercode];
      },
      sameDayDeliveryCutOffLabelId() {
        return GET_PHARMACY_SAME_DAY_DELIVERY_CUTOFF_LABEL_ID(this.serviceOrder.partnercode);
      },
      street() {
        return this.serviceOrder.address ? this.serviceOrder.address.street : null;
      },
      city() {
        return this.serviceOrder.address ? this.serviceOrder.address.city : null;
      },
    },
    methods: {
      showContactNumberModal() {
        this.$modal.open({
          component: ModalContactNumber,
          position: 'detached auto-height',
          size: 'xs',
          props: {
            branch: this.selectedWatson,
          }
        });
      },
      async viewId(id) {
        this.loading = true;

        try {
          const {data} = await MedicineService.getOrderFile(this.serviceOrder.orderid, id);

          if (data.url) {
            this.$modal.open({
              component: ModalPreviewFile,
              props: {
                file: [data.url],
                title: this.getElementString('txt111'),
                showButton: false
              },
            });
          } else {
            throw Error();
          }

          this.loading = false;
        } catch (error) {
          this.loading = false;
          this.showToast({
            text: 'Failed to fetch order. Please try again later.',
            theme: 'danger',
          });
        }
      },
      computedHideCoupon(item) {
        return item.coupons && item.coupons[0] ? isCouponExpired(item.coupons[0]) : true;
      },
      isIssuedCoupon(item) {
        const possibleValues = ['issued', 'availed'];
        return possibleValues.includes(item.coupons ? item.coupons.length ? item.coupons[0].status.toLowerCase() : '' : '');
      },
    },
  }
</script>

<style lang="scss">
  @import "~@/assets/styles/util.scss";

  .medicine-order-details-watsons-delivery {
    padding: 16px 20px;

    @include responsive(desktop) {
      @include border-radius();
      @include box-shadow();
      background: color(white);
      padding: space(xl);
      margin: 40px 40px 40px 40px;
      max-width: 900px;
    }

    @include responsive('max-width: 1023px') {
      margin-left: 0 !important;
    }
    @include responsive('min-width: 1800px') {
      margin: 40px auto 40px;
      width: 100%;
    }

    .view-delivery-call__call-btn {
      @include responsive(desktop) {
        max-width: 400px;
      }

      .text-string span {
        @include font-style(Button2)
      }
    }
  }
</style>
